/**
 * عروضي — shared constants for the template kit. Values are lifted from the
 * approved prototype (.claude/promos-prototype.html): palettes, QR copy,
 * shape geometry. Nothing here is engine data; the engine keeps its own
 * shape enum (corner | bottom | lshape).
 */

/** UI shape key -> backend enum value. 'window' has no backend value yet (player build pending). */
export const SHAPE_KEYS = ['lshape', 'bottom', 'corner', 'window'];
export const SHAPE_ENABLED = { lshape: true, bottom: true, corner: true, window: false };

/** Exact creative size the player maps each shape from (MEASURED: LShapeAdOverlayView / BottomAdOverlayView / CornerAdOverlayView). */
export const SHAPE_SIZE = {
  lshape: { w: 1920, h: 1080 },
  bottom: { w: 1920, h: 216 },
  corner: { w: 400, h: 400 },
};

export const PALETTES = [
  { id: 'honey', a: '#E9A23E', a2: '#B9701C', bg1: '#1C130A', bg2: '#0E0A06', ink: '#F7EFE2', dim: '#CDBFA8', onA: '#1A120A' },
  { id: 'jade', a: '#2BB585', a2: '#157A58', bg1: '#0F1D17', bg2: '#08110D', ink: '#EEF7F2', dim: '#B7CFC4', onA: '#06140E' },
  { id: 'coral', a: '#F06A5E', a2: '#B23A30', bg1: '#22100E', bg2: '#120807', ink: '#FBEDEA', dim: '#DDBDB8', onA: '#1E0705' },
  { id: 'night', a: '#7FA7FF', a2: '#3D5FB8', bg1: '#111827', bg2: '#090D16', ink: '#EEF2FB', dim: '#B8C2D8', onA: '#0A1022' },
  { id: 'cream', a: '#B9701C', a2: '#8A4E12', bg1: '#FBF5EA', bg2: '#EFE3CF', ink: '#22190F', dim: '#6E5B44', onA: '#FFF8EC' },
  { id: 'gold', a: '#D4AF37', a2: '#8C6D12', bg1: '#151310', bg2: '#070605', ink: '#F8F1DC', dim: '#C9BC97', onA: '#140F02' },
  { id: 'pitch', a: '#3DDC84', a2: '#178A4C', bg1: '#0B2416', bg2: '#04120A', ink: '#F1FBF5', dim: '#B4D8C3', onA: '#03170B' },
  { id: 'sport', a: '#FF3B30', a2: '#B3150C', bg1: '#1A0605', bg2: '#0A0202', ink: '#FFF1EF', dim: '#E2BAB6', onA: '#FFFFFF' },
  { id: 'violet', a: '#A98BFF', a2: '#6A47D6', bg1: '#171127', bg2: '#0B0816', ink: '#F3EFFF', dim: '#C6BCE6', onA: '#140B33' },
  { id: 'candy', a: '#FF7EB6', a2: '#C23F7A', bg1: '#2A0F1E', bg2: '#16060F', ink: '#FFF0F6', dim: '#E6BACD', onA: '#2B0717' },
  { id: 'teal', a: '#2EC4B6', a2: '#13786F', bg1: '#082025', bg2: '#031114', ink: '#EAFBF9', dim: '#AFD6D2', onA: '#02201D' },
  { id: 'latte', a: '#6F4E37', a2: '#4A3222', bg1: '#F7EFE4', bg2: '#E9DAC6', ink: '#2A1C12', dim: '#6E5847', onA: '#FFF6EA' },
];
/** Light backgrounds need an outline on the QR card and a softer product shadow (cream, latte). */
export const isLightPalette = (p) => {
  const n = parseInt(p.bg1.slice(1), 16);
  const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255) > 0.5;
};
export const paletteById = (id) => PALETTES.find((p) => p.id === id) || PALETTES[0];

export const CURRENCIES = [
  { code: 'SAR', sym: 'ر.س' },
  { code: 'JOD', sym: 'د.أ' },
  { code: 'EGP', sym: 'ج.م' },
];
export const currencyByCode = (code) => CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

/** QR destinations offered in this build (static, counted via /q/{code}) + wifi (direct, uncounted). */
export const QR_TYPES = [
  { key: 'none' }, { key: 'instagram' }, { key: 'snapchat' }, { key: 'google' },
  { key: 'whatsapp' }, { key: 'delivery' }, { key: 'wifi' }, { key: 'link' },
];
/** Placeholder text per type is the i18n key promos.v2_qrph_<key>; labels promos.v2_qr_<key>. */
export const qrType = (key) => QR_TYPES.find((q) => q.key === key) || QR_TYPES[0];

export const MAX_DESIGNS = 6;
export const TITLE_MAX = 24;
export const LINE_MAX = 44;

/**
 * How the PLAYER brings a creative on and off screen (MEASURED, read-only):
 *  - lshape: the right strip slides in from the right and the bottom strip up from the bottom, 500 ms, ease (0.4,0,0.2,1)
 *            (LShapeAdOverlayView.kt SLIDE_MS :61, EASE :64; exit is the same slide reversed)
 *  - bottom: the banner slides up from the bottom edge, 400 ms, same ease (BottomAdOverlayView.kt SLIDE_MS :56, EASE :61)
 *  - corner: a fade, 300 ms (CornerAdOverlayView.kt FADE_MS :65)
 * One APPEARANCE = one design: in, held for show_duration_seconds, out; the next appearance (interval_seconds later) takes the next design.
 * The preview's «شاهد التسلسل» plays them back to back with a compressed hold (SEQ_HOLD_MS) and these real transition lengths.
 */
export const PLAYER_TRANSITION = {
  lshape: { ms: 500, kind: 'slide', ease: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  bottom: { ms: 400, kind: 'slide', ease: 'cubic-bezier(0.4, 0, 0.2, 1)' },
  corner: { ms: 300, kind: 'fade', ease: 'ease-in-out' },
};
export const SEQ_HOLD_MS = 1600;
